import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Header, ScreenLayout, Input, PhoneInput, OTPInput, Button, colors, typography } from '../components';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { apiService } from '../services/api';
import { tokenManager } from '../services/token-manager';
import { handleApiError, logError } from '../utils/errorHandler';
import { useLocale } from '../contexts/LocaleContext';
import { useTypography } from '../hooks/useTypography';
import { useResponsive } from '../utils/responsive';
import { useThemedStyles } from '../hooks/useThemedStyles';

const RESEND_COOLDOWN_SECONDS = 60;

/**
 * "Complete your profile" second-auth-method flow: a user who registered with only phone OR
 * only email is missing the other verified identity. This screen always requires a genuine OTP
 * round trip before isEmailVerified/isMobileVerified flips true — never an unverified form —
 * so a user can never claim someone else's email/mobile without proving ownership of it.
 *
 * Driven by routeParams.identityType ('email' | 'mobile'), set by HomeScreen's
 * handleCompleteService when the profile-completion widget's identity_email/identity_mobile
 * step is tapped. Uses the same ScreenLayout/Header shell as every other in-app (post-login)
 * screen — not DualPaneLayout, which is reserved for the pre-login marketing flow.
 */
export const AddVerifyIdentityScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, routeParams, activeTab, setActiveTab } = useAppContext();
  const { setUser, user } = useAppStore();
  const { t } = useLocale();
  const typo = useTypography();
  const { isWeb, webResponsive, scale } = useResponsive();

  const identityType = ((routeParams as any)?.identityType as 'email' | 'mobile') || 'email';
  const isEmail = identityType === 'email';

  const [step, setStep] = useState<'input' | 'otp'>('input');
  const [value, setValue] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [showLeftPane, setShowLeftPane] = useState(Platform.OS === 'web');

  useEffect(() => {
    setActiveTab('home');
  }, [setActiveTab]);

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    };
  }, []);

  const startResendCooldown = () => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    cooldownTimerRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const handleBack = () => {
    if (step === 'otp') {
      setStep('input');
      setOtpCode('');
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
      setResendCooldown(0);
      return;
    }
    navigateTo('home');
  };

  const isValidInput = isEmail
    ? /\S+@\S+\.\S+/.test(value.trim())
    : value.trim().length >= 6;

  const handleSendOtp = async () => {
    if (!value.trim()) {
      showNotification('warning', t('identity.requiredTitle'), isEmail ? t('identity.requiredEmail') : t('identity.requiredMobile'));
      return;
    }
    if (!isValidInput) {
      showNotification('warning', t('identity.invalidTitle'), isEmail ? t('identity.invalidEmail') : t('identity.invalidMobile'));
      return;
    }

    setIsLoading(true);
    try {
      const response = isEmail
        ? await apiService.sendAttachEmailOTP(value.trim())
        : await apiService.sendAttachPhoneOTP(value.trim());

      if (response.statusCode === 1000) {
        setStep('otp');
        startResendCooldown();
        showNotification('success', t('identity.codeSentTitle'), isEmail ? t('identity.codeSentEmail') : t('identity.codeSentMobile'));
      } else {
        const { title, message, type } = handleApiError(response);
        showNotification(type, title, message);
      }
    } catch (error: any) {
      logError(error, 'AddVerifyIdentityScreen');
      const { title, message, type } = handleApiError(error);
      showNotification(type, title, message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setIsLoading(true);
    try {
      if (isEmail) {
        await apiService.sendAttachEmailOTP(value.trim());
      } else {
        await apiService.sendAttachPhoneOTP(value.trim());
      }
      startResendCooldown();
      showNotification('success', t('identity.codeSentTitle'), t('identity.codeResent'));
    } catch (error: any) {
      logError(error, 'AddVerifyIdentityScreen');
      const { title, message, type } = handleApiError(error);
      showNotification(type, title, message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    if (otpCode.length !== 6) {
      showNotification('warning', t('identity.invalidCodeTitle'), t('identity.invalidCodeMessage'));
      return;
    }

    setIsLoading(true);
    try {
      const response = isEmail
        ? await apiService.verifyAttachEmailOTP(value.trim(), otpCode)
        : await apiService.verifyAttachPhoneOTP(value.trim(), otpCode);

      if (response.statusCode === 1000 && response.data) {
        showNotification('success', t('identity.verifiedTitle'), isEmail ? t('identity.verifiedEmail') : t('identity.verifiedMobile'));

        // The backend only updated the User document — refresh the token so the JWT's
        // emailVerified/mobileVerified claims (and useProfileCompletion, which reads them)
        // reflect the change immediately instead of waiting for the next natural refresh.
        try {
          await tokenManager.refreshTokens();
        } catch (refreshError) {
          logError(refreshError, 'AddVerifyIdentityScreen:refreshTokens');
        }

        if (user) {
          setUser({
            ...user,
            ...(isEmail ? { email: value.trim(), isEmailVerified: true } : { mobile: value.trim(), phone: value.trim(), isMobileVerified: true })
          });
        }

        navigateTo('home');
      } else {
        const { title, message, type } = handleApiError(response);
        showNotification(type, title, message);
      }
    } catch (error: any) {
      logError(error, 'AddVerifyIdentityScreen');
      const { title, message, type } = handleApiError(error);
      showNotification(type, title, message);
    } finally {
      setIsLoading(false);
    }
  };

  const contentMaxWidth = isWeb ? Math.min(600, webResponsive.maxWidth) : undefined;

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={false}
          showMessages={false}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      showLeftPane={showLeftPane}
      onLeftPaneClose={() => setShowLeftPane(false)}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      rightPaneContent="empty"
    >
      <ScrollView
        contentContainerStyle={[styles.scrollContent, { paddingHorizontal: scale(20) }]}
        keyboardShouldPersistTaps="handled"
      >
        <View style={[styles.content, contentMaxWidth ? { maxWidth: contentMaxWidth, width: '100%', alignSelf: 'center' } : null]}>
          <View style={[styles.iconCircle, { backgroundColor: colors.background.surface }]}>
            <Ionicons name={isEmail ? 'mail-outline' : 'call-outline'} size={scale(32)} color={colors.primary as string} />
          </View>

          {step === 'input' ? (
            <>
              <Text style={[styles.title, { fontFamily: typo.fontFamily.bold, fontSize: scale(typography.fontSize['2xl']) }]}>
                {isEmail ? t('identity.addEmailTitle') : t('identity.addMobileTitle')}
              </Text>
              <Text style={[styles.subtitle, { fontFamily: typo.fontFamily.primary }]}>
                {isEmail ? t('identity.addEmailSubtitle') : t('identity.addMobileSubtitle')}
              </Text>

              {isEmail ? (
                <Input
                  value={value}
                  onChangeText={setValue}
                  placeholder="you@example.com"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  latin
                  containerStyle={styles.inputContainer}
                  editable={!isLoading}
                  onSubmitEditing={handleSendOtp}
                  returnKeyType="send"
                />
              ) : (
                <PhoneInput
                  value={value}
                  onChangeText={setValue}
                  style={styles.inputContainer}
                  disabled={isLoading}
                />
              )}

              <Button
                title={t('identity.sendCode')}
                onPress={handleSendOtp}
                variant="primary"
                size="lg"
                fullWidth
                loading={isLoading}
                disabled={!value.trim()}
                style={styles.actionButton}
              />
            </>
          ) : (
            <>
              <Text style={[styles.title, { fontFamily: typo.fontFamily.bold, fontSize: scale(typography.fontSize['2xl']) }]}>
                {t('identity.enterCodeTitle')}
              </Text>
              <Text style={[styles.subtitle, { fontFamily: typo.fontFamily.primary }]}>
                {isEmail ? t('identity.enterCodeSubtitleEmail') : t('identity.enterCodeSubtitleMobile')}
              </Text>
              <Text style={styles.value}>{value}</Text>

              <OTPInput
                value={otpCode}
                onChangeText={setOtpCode}
                length={6}
                disabled={isLoading}
                autoFocus
                onComplete={() => handleVerify()}
              />

              <Button
                title={t('identity.verify')}
                onPress={handleVerify}
                variant="primary"
                size="lg"
                fullWidth
                loading={isLoading}
                disabled={otpCode.length !== 6}
                style={styles.actionButton}
              />

              <TouchableOpacity
                onPress={handleResend}
                disabled={isLoading || resendCooldown > 0}
                style={styles.resendButton}
              >
                <Text style={[
                  styles.resendText,
                  { fontFamily: typo.fontFamily.medium },
                  (isLoading || resendCooldown > 0) && styles.disabledText
                ]}>
                  {resendCooldown > 0
                    ? t('identity.resendCodeIn', { seconds: String(resendCooldown) })
                    : t('auth.resendCode')}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity onPress={handleBack} disabled={isLoading} style={styles.changeValueButton}>
                <Text style={[styles.changeValueText, { fontFamily: typo.fontFamily.medium }]}>
                  {isEmail ? t('identity.changeEmail') : t('identity.changeMobile')}
                </Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </ScrollView>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    paddingTop: 24,
    paddingBottom: 48,
    alignItems: 'center',
  },
  content: {
    alignItems: 'center',
  },
  iconCircle: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  title: {
    fontWeight: '700',
    color: colors.textDark,
    marginBottom: 12,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    marginBottom: 8,
    textAlign: 'center',
  },
  value: {
    fontSize: typography.fontSize.base,
    color: colors.primary,
    marginBottom: 24,
    textAlign: 'center',
    fontWeight: '600',
  },
  inputContainer: {
    width: '100%',
    marginTop: 24,
  },
  actionButton: {
    marginTop: 24,
    marginBottom: 16,
    width: '100%',
  },
  resendButton: {
    marginTop: 8,
  },
  resendText: {
    color: colors.primary,
    fontSize: typography.fontSize.sm,
  },
  changeValueButton: {
    marginTop: 16,
  },
  changeValueText: {
    color: colors.textLight,
    fontSize: typography.fontSize.sm,
  },
  disabledText: {
    color: colors.textLight,
  },
});
